/**
 * FeeConfigurationErrorBoundary.tsx
 * Error boundary component for fee configuration to catch type assertion failures
 * and provide graceful error handling
 */

import React, { Component, ReactNode } from 'react';
import { Card, CardBody, Button, Alert } from '@enfomentis/ui';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class FeeConfigurationErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Fee Configuration Error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Card className="max-w-2xl mx-auto mt-8">
          <CardBody className="text-center py-8">
            <AlertTriangle className="w-16 h-16 text-danger mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-text-primary mb-2">
              Fee Configuration Error
            </h3>
            <p className="text-text-secondary mb-6">
              Something went wrong while loading the fee configuration. This is likely due to corrupted data or a system error.
            </p>

            <Alert
              color="danger"
              variant="bordered"
              className="mb-6 text-left"
              title="Technical Details"
              description={
                <div className="mt-2">
                  <p className="text-sm font-mono bg-danger-50 p-2 rounded">
                    {this.state.error?.message || 'Unknown error occurred'}
                  </p>
                </div>
              }
            />

            <div className="flex gap-3 justify-center">
              <Button
                color="primary"
                startContent={<RefreshCw className="w-4 h-4" />}
                onPress={this.handleReset}
              >
                Try Again
              </Button>
              <Button
                variant="bordered"
                onPress={() => window.location.reload()}
              >
                Refresh Page
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    }

    return this.props.children;
  }
}

export const FeeConfigurationError: React.FC = () => (
  <Card className="max-w-2xl mx-auto mt-8">
    <CardBody className="text-center py-8">
      <AlertTriangle className="w-16 h-16 text-danger mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-text-primary mb-2">
        Fee Configuration Unavailable
      </h3>
      <p className="text-text-secondary mb-6">
        The fee configuration system is currently unavailable. Please try refreshing the page.
      </p>
      <Button
        color="primary"
        onPress={() => window.location.reload()}
        startContent={<RefreshCw className="w-4 h-4" />}
      >
        Refresh Page
      </Button>
    </CardBody>
  </Card>
);